import React from 'react';
import { Offcanvas, Form, Button, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../../config/api';
import { Box, Divider, Typography } from "@mui/material";
import {
  CameraReelsFill,
  FileEarmarkTextFill,
  Youtube,
  ImageFill,
  Upload
} from "react-bootstrap-icons";

// Reusable SectionHeader component
const SectionHeader = ({ icon, label }) => (
  <Box display="flex" alignItems="center" gap={1} mb={2} mt={2}>
    <Box sx={{ color: "#0d47a1", display: "flex" }}>{icon}</Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#7b8ab8",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "#dde4f5" }} />
  </Box>
);

// Reusable field style
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
  padding: "10px",
};

const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

const AddMediaOffcanvas = ({ show, onSuccess, handleClose, classId }) => {
  const { 
    register, 
    handleSubmit, 
    watch, 
    reset, 
    formState: { errors, isSubmitting } 
  } = useForm();

  const onSubmit = async (data) => {
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('Description', data.Description);
      
      if (data.uploadVideo?.[0]) {
        formData.append('uploadVideo', data.uploadVideo[0]);
      }
      if (data.uploadLink) {
        formData.append('uploadLink', data.uploadLink);
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/mediaAdmin/addMedia/${classId}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Media added successfully!');
      reset();
      handleClose();
      onSuccess();

    } catch (error) {
      console.error('Error adding media:', error);
      toast.error(error.response?.data?.message || 'Failed to add media');
    }
  };

  const validateMediaSource = () => {
    const videoFile = watch('uploadVideo')?.[0];
    const youtubeLink = watch('uploadLink');
    return !!videoFile || !!youtubeLink || 'Either video file or YouTube link is required';
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      style={{ width: 450 }}
    >
      <Offcanvas.Header
        closeButton
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #1a237e, #0d47a1)",
          borderBottom: "none",
        }}
      >
        <Offcanvas.Title
          className="d-flex align-items-center gap-2 fw-bold"
          style={{ fontSize: "1rem" }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: "rgba(255,235,59,0.2)",
              border: "1.5px solid rgba(255,235,59,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CameraReelsFill size={13} color="#ffeb3b" />
          </Box>
          Add Media to Class
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ background: "#f0f4ff" }}>
        <Form onSubmit={handleSubmit(onSubmit)} className="p-3 rounded-3 shadow-sm bg-white">
          
          <SectionHeader
            icon={<FileEarmarkTextFill size={13} />}
            label="Media Information"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control 
              {...register('title', { required: 'Title is required' })}
              placeholder="Enter media title"
              style={fieldStyle}
              isInvalid={!!errors.title}
            />
            {errors.title && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.title.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label style={labelStyle}>
              Description <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={100}
              {...register('Description', { 
                required: 'Description is required',
                maxLength: {
                  value: 100,
                  message: 'Description cannot exceed 100 characters'
                }
              })}
              placeholder="Enter description (max 100 characters)"
              style={fieldStyle}
              isInvalid={!!errors.Description}
            />
            {errors.Description && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.Description.message}
              </div>
            )}
            <Form.Text className="text-muted" style={{ fontSize: "0.75rem", color: "#7b8ab8" }}>
              {watch('Description')?.length || 0}/100 characters
            </Form.Text>
          </Form.Group>

          {/* <SectionHeader
            icon={<Upload size={13} />}
            label="Media Source"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              <CameraReelsFill size={12} className="me-1" /> Upload Video
            </Form.Label>
            <Form.Control 
              type="file" 
              accept="video/*" 
              {...register('uploadVideo', {
                validate: validateMediaSource
              })}
              style={fieldStyle}
            />
            <Form.Text className="text-muted" style={{ fontSize: "0.75rem", color: "#7b8ab8" }}>
              Max 2 minutes, MP4 or MOV format
            </Form.Text>
          </Form.Group> */}

          {/* <Box display="flex" alignItems="center" justifyContent="center" my={2}>
            <Divider sx={{ width: "40%" }} />
            <Typography variant="caption" sx={{ mx: 2, color: "#7b8ab8", fontWeight: 600 }}>
              OR
            </Typography>
            <Divider sx={{ width: "40%" }} />
          </Box> */}

          <Form.Group className="mb-4">
            <Form.Label style={labelStyle}>
              <Youtube size={12} className="me-1" color="#ff0000" /> YouTube Link
            </Form.Label>
            <Form.Control 
              type="url" 
              placeholder="https://youtube.com/watch?v=..." 
              {...register('uploadLink', {
                validate: validateMediaSource
              })}
              style={fieldStyle}
              isInvalid={!!errors.uploadLink}
            />
            {(errors.uploadVideo || errors.uploadLink) && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.uploadVideo?.message || errors.uploadLink?.message}
              </div>
            )}
          </Form.Group>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
            style={{
              background: "linear-gradient(135deg, #1a237e, #0d47a1)",
              border: "none",
              borderRadius: "10px",
              padding: "11px",
              boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
              marginTop: "20px"
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" animation="border" /> Uploading Media...
              </>
            ) : (
              <>
                <CameraReelsFill size={15} /> Add Media
              </>
            )}
          </Button>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddMediaOffcanvas;